import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { useSnackbar } from "notistack";
import { useTonConnect } from "@/hooks/useTonConnect";
import { formatTonAddress } from "@/utils/common";
import { backend } from "@/services/backend";
import { recoverRefParam } from "@/utils/telegram";
import { enqueueSnackbarWithClose } from "@/utils/snackbar";

interface Wallet {
  account: {
    address: string;
  };
  disconnect: () => Promise<void>;
}

interface WalletContextType {
  wallet: Wallet | null;
  isConnected: boolean;
  disconnect: () => Promise<void>;
  isMenuOpen: boolean;
  toggleMenu: () => void;
  walletAddress: string | null;
}

const isInTelegram = () => {
  return window.Telegram && window.Telegram.WebApp;
};

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const { wallet } = useTonConnect() as { wallet: Wallet | null };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tonConnectUI] = useTonConnectUI();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const walletAddress = useMemo(() => {
    if (!wallet) return null;
    const address =
      typeof wallet === "string" ? wallet : wallet.account.address;
    return formatTonAddress(address);
  }, [wallet]);

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  useEffect(() => {
    const checkReferral = async () => {
      if (wallet) {
        try {
          if (isInTelegram() && window.Telegram?.WebApp?.initData) {
            const result = await backend.saveTelegram(
              window.Telegram.WebApp.initData,
              walletAddress!
            );
          }
          //.replace('%3F', '?')
          const ref = recoverRefParam(document.location.pathname);
          const urlParams = new URLSearchParams(window.location.search);
          const refAddress = urlParams.get("ref");

          if (ref || refAddress) {
            await backend.registerReferral(
              walletAddress!,
              ref || refAddress || ""
            );

            enqueueSnackbarWithClose(
              enqueueSnackbar,
              closeSnackbar,
              "Referral registered successfully!",
              {
                variant: "success",
                autoHideDuration: 2000,
              }
            );
          }

          enqueueSnackbarWithClose(
            enqueueSnackbar,
            closeSnackbar,
            "Wallet connected successfully!",
            {
              variant: "success",
              autoHideDuration: 2000,
            }
          );
        } catch (error) {
          console.error("Failed to process referral:", error);
          enqueueSnackbarWithClose(
            enqueueSnackbar,
            closeSnackbar,
            "Failed to process referral",
            {
              variant: "error",
              autoHideDuration: 2000,
            }
          );
        }
      }
    };
    checkReferral();
  }, [wallet, walletAddress]);

  const disconnect = async () => {
    try {
      // Check if wallet is actually connected before attempting to disconnect
      if (tonConnectUI.wallet) {
        await tonConnectUI.disconnect();
        enqueueSnackbarWithClose(
          enqueueSnackbar,
          closeSnackbar,
          "Wallet is disconnected!",
          {
            variant: "info",
            autoHideDuration: 1000,
          }
        );
      } else {
        console.warn("No wallet connected to disconnect");
      }
    } catch (error) {
      console.error("Failed to disconnect:", error);
    }
  };

  return (
    <WalletContext.Provider
      value={{
        wallet,
        isConnected: !!wallet,
        disconnect,
        isMenuOpen,
        toggleMenu,
        walletAddress,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
}
